import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchCourses } from "@/lib/courses";
import { Course } from "@/lib/types";  // Updated import
import { formatPrice } from "@/lib/utils";

export type EnrolledCourse = Course & {
  progress: number;
  hasAccess?: boolean;
  price?: string;
};

interface UseEnrolledCoursesProps {
  userId: string | undefined;
  authLoading: boolean;
}

export function useEnrolledCourses({ userId, authLoading }: UseEnrolledCoursesProps) {
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(true);
  
  const fetchEnrolledCourses = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    
    try {
      // Get courses the user has access to, which now includes progress
      const { data: accessData, error: accessError } = await supabase
        .from("user_course_access")
        .select("course_slug, progress, completed_sections")
        .eq("user_id", userId);
      
      if (accessError) {
        console.error("Error fetching course access:", accessError);
        setEnrolledCourses([]);
        setLoading(false);
        return;
      }
      
      // Get all courses from all languages
      const englishCourses = await fetchCourses('en');
      const frenchCourses = await fetchCourses('fr'); 
      const allCourses = [...englishCourses, ...frenchCourses];
      
      // Get enrolled course slugs
      const enrolledSlugs = accessData?.map(item => item.course_slug) || [];
      
      // Filter to only enrolled courses
      const userCourses = allCourses.filter(course => 
        enrolledSlugs.includes(course.slug)
      );
      
      // Map progress to courses
      const coursesWithProgress: EnrolledCourse[] = userCourses.map(course => {
        // Find the matching access record to get progress
        const accessRecord = accessData?.find(item => item.course_slug === course.slug);
        const progress = accessRecord?.progress || 0;
        
        // Add required properties
        return { 
          ...course, 
          progress,
          hasAccess: true, // They have access if they're enrolled
          price: course.default_price?.unit_amount 
            ? formatPrice(course.default_price.unit_amount, course.default_price.currency)
            : course.is_free ? "Free" : undefined
        };
      });
      
      setEnrolledCourses(coursesWithProgress);
    } catch (error) {
      console.error("Error fetching enrolled courses:", error);
      setEnrolledCourses([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);
  
  useEffect(() => {
    if (authLoading) return;
    
    if (!userId) {
      // No user is logged in, so no enrolled courses.
      setEnrolledCourses([]);
      setLoading(false);
      return;
    }
    
    fetchEnrolledCourses();

    // Set up a subscription to course access changes
    const channel = supabase
      .channel(`user_course_changes_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_course_access',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Course access changed:', payload);
          // Refetch courses when course access changes
          fetchEnrolledCourses();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, authLoading, fetchEnrolledCourses]);

  return {
    enrolledCourses,
    loading,
    refetch: fetchEnrolledCourses
  };
}


